import { defineStore } from 'pinia'
import { markRaw } from 'vue'
import * as vault from '../lib/vault'
import { detectNewNames, makePerson } from '../lib/people'
import { PEOPLE as SEED_PEOPLE, LINKS as SEED_LINKS, COMPANIES as SEED_COMPANIES, type Person, type Link, type Dept, type Company } from '../data/demo'

export interface Att { id: string; kind: 'voice' | 'image'; sec?: number; ocr?: string }
export interface Entry { id: string; text: string; date: string; people?: string[]; insight?: string; att?: Att[] }
export type Relation = Link & { id: string }

// 금고 상태. CryptoKey는 메모리에만 있고 어디에도 저장하지 않는다(새로고침하면 다시 잠긴다).
// 사람·부서·관계·일기 모두 암호화되어 IndexedDB에만 있다(goals.md 로컬 우선·원칙 1).
export const useVaultStore = defineStore('vault', {
  state: () => ({
    initialized: false,
    key: null as CryptoKey | null,
    checked: false,
    people: [] as Person[],
    depts: [] as Dept[],
    companies: [] as Company[], // 외부 조직(고객사)
    relations: [] as Relation[],
    entries: [] as Entry[],
    _attUrls: new Map<string, string>(), // 복호화한 첨부 data URL 캐시(메모리)
  }),
  getters: {
    locked: (s) => s.key === null,
  },
  actions: {
    async init() {
      this.initialized = await vault.isInitialized()
      this.checked = true
    },
    async setup(password: string) {
      const k = markRaw(await vault.setup(password))
      for (const p of SEED_PEOPLE) await vault.putRecord(k, 'person', p.id, p)
      for (const c of SEED_COMPANIES) await vault.putRecord(k, 'company', c.id, c)
      for (const l of SEED_LINKS) {
        const id = crypto.randomUUID()
        await vault.putRecord(k, 'relation', id, { ...l, id })
      }
      await this.loadAll(k)
      this.key = k
      this.initialized = true
    },
    async unlock(password: string) {
      const k = markRaw(await vault.unlock(password))
      await this.loadAll(k)
      this.key = k
    },
    async loadAll(k: CryptoKey) {
      this.people = await vault.getRecords<Person>(k, 'person')
      this.depts = await vault.getRecords<Dept>(k, 'dept')
      this.companies = await vault.getRecords<Company>(k, 'company')
      this.relations = await vault.getRecords<Relation>(k, 'relation')
      this.entries = await vault.getRecords<Entry>(k, 'entry')
    },
    lock() {
      this.key = null
      this.people = []; this.depts = []; this.companies = []; this.relations = []; this.entries = []
      this._attUrls.clear()
    },
    // 동기화 복원: 서버에서 받은 스냅샷으로 금고를 교체(기기 키로 재암호화). 첨부 바이너리는 v1 제외.
    async replaceAll(s: { people?: Person[]; depts?: Dept[]; companies?: Company[]; relations?: Relation[]; entries?: Entry[] }) {
      if (!this.key) throw new Error('locked')
      const tables: Array<[string, any[]]> = [
        ['person', s.people || []], ['dept', s.depts || []], ['company', s.companies || []],
        ['relation', s.relations || []], ['entry', s.entries || []],
      ]
      for (const [table] of tables) {
        const existing = await vault.getRecords<{ id: string }>(this.key, table)
        for (const r of existing) await vault.deleteRecord(table, r.id)
      }
      for (const [table, rows] of tables) for (const r of rows) await vault.putRecord(this.key, table, r.id, r)
      await this.loadAll(this.key)
    },
    // 위장 해제용: 입력한 비번이 금고 비번과 맞는지 확인(키는 안 건드림)
    verifyPassword(pw: string) {
      return vault.verify(pw)
    },
    // 비밀번호 변경: 현재 비번 확인 후 전체를 새 키로 재암호화
    async changePassword(oldPw: string, newPw: string): Promise<boolean> {
      if (!this.key || !(await vault.verify(oldPw))) return false
      this.key = markRaw(await vault.changePassword(this.key, newPw))
      this._attUrls.clear()
      return true
    },
    async wipeAll() {
      await vault.wipe()
      this.key = null; this.initialized = false
      this.people = []; this.depts = []; this.companies = []; this.relations = []; this.entries = []
    },

    // ── 일기 ──
    async addEntry(text: string, people: string[] = [], insight?: string, att?: Att[]) {
      if (!this.key) throw new Error('locked')
      const entry: Entry = { id: crypto.randomUUID(), text, date: new Date().toISOString(), people, insight, att }
      await vault.putRecord(this.key, 'entry', entry.id, entry)
      this.entries.push(entry)
      return entry
    },
    // 첨부 바이너리(음성·이미지)도 암호화해 저장한다. 서버로 가지 않는다.
    async addAttachment(kind: 'voice' | 'image', mime: string, b64: string) {
      if (!this.key) throw new Error('locked')
      const id = crypto.randomUUID()
      await vault.putRecord(this.key, 'attachment', id, { kind, mime, b64 })
      return id
    },
    async getAttachmentUrl(id: string): Promise<string | null> {
      if (this._attUrls.has(id)) return this._attUrls.get(id)!
      if (!this.key) return null
      const r = await vault.getRecord<{ mime: string; b64: string }>(this.key, 'attachment', id)
      if (!r) return null
      const url = `data:${r.mime};base64,${r.b64}`
      this._attUrls.set(id, url)
      return url
    },
    // 일기 저장 + 자동 학습: 이름 태그, 2명+키워드면 관계 발견(지도에 반영), 인사이트 한 줄.
    async logEntry(text: string, att?: Att[]) {
      const found = this.people.filter(p => p.name && text.includes(p.name))
      const ids = found.map(p => p.id)
      let insight = found.length ? `${found.map(p => p.name).join(', ')} 기록 +1` : '기록 +1'
      if (found.length >= 2) {
        const type: 'friend' | 'tension' | null = /싸|부딪|안 맞|사이 안|신경전|긴장|껄끄/.test(text)
          ? 'tension' : /친|같이|동기|편|챙겨|도와|밥|술/.test(text) ? 'friend' : null
        if (type) {
          const [a, b] = [found[0].id, found[1].id]
          if (!this.relations.some(r => (r.a === a && r.b === b) || (r.a === b && r.b === a))) {
            await this.addRelation(a, b, type, '일기에서 발견')
            insight = `새 관계 발견 · ${found[0].name}–${found[1].name} ${type === 'friend' ? '친함' : '긴장'}`
          }
        }
      }
      const entry = await this.addEntry(text, ids, insight, att)
      // 아직 등록 안 된 사람 후보(규칙 기반) — UI가 "추가할까요?"로 물어본다(사용자 확인)
      const newNames = detectNewNames(text, this.people)
      return { entry, insight, foundIds: ids, newNames }
    },

    // ── 사람 ──
    // 기본값으로 새 사람을 만들어 저장한다(일기 새 사람 포착·빠른 추가). 색은 사람 수로 정한다.
    async createPerson(name: string, extra: Partial<Person> = {}) {
      const p = makePerson(name, this.people.length, extra)
      await this.savePerson(p)
      return p
    },
    async savePerson(p: Person) {
      if (!this.key) throw new Error('locked')
      await vault.putRecord(this.key, 'person', p.id, { ...p })
      const i = this.people.findIndex(x => x.id === p.id)
      if (i >= 0) this.people[i] = p; else this.people.push(p)
    },
    async deletePerson(id: string) {
      if (!this.key) throw new Error('locked')
      await vault.deleteRecord('person', id)
      this.people = this.people.filter(p => p.id !== id)
      // 이 사람이 낀 관계도 지운다
      for (const r of this.relations.filter(r => r.a === id || r.b === id)) await vault.deleteRecord('relation', r.id)
      this.relations = this.relations.filter(r => r.a !== id && r.b !== id)
      // 이 사람을 상사로 두던 사람의 boss를 비운다
      for (const p of this.people) if (p.boss === id) { p.boss = undefined; await vault.putRecord(this.key, 'person', p.id, { ...p }) }
    },

    // ── 부서 ──
    async saveDept(dept: Dept) {
      if (!this.key) throw new Error('locked')
      await vault.putRecord(this.key, 'dept', dept.id, { ...dept })
      const i = this.depts.findIndex(d => d.id === dept.id)
      if (i >= 0) this.depts[i] = dept; else this.depts.push(dept)
    },
    async deleteDept(id: string) {
      if (!this.key) throw new Error('locked')
      await vault.deleteRecord('dept', id)
      this.depts = this.depts.filter(d => d.id !== id)
      // 그 부서 소속이던 사람은 소속 없음으로
      for (const p of this.people) if (p.dept === id) { p.dept = null; await vault.putRecord(this.key, 'person', p.id, { ...p }) }
    },

    // ── 외부 조직(고객사) ──
    async saveCompany(c: Company) {
      if (!this.key) throw new Error('locked')
      await vault.putRecord(this.key, 'company', c.id, { ...c })
      const i = this.companies.findIndex(x => x.id === c.id)
      if (i >= 0) this.companies[i] = c; else this.companies.push(c)
    },
    async deleteCompany(id: string) {
      if (!this.key) throw new Error('locked')
      await vault.deleteRecord('company', id)
      this.companies = this.companies.filter(c => c.id !== id)
      // 그 회사 소속이던 사람은 내 회사(company 없음)로 되돌린다
      for (const p of this.people) if (p.company === id) { p.company = null; await vault.putRecord(this.key, 'person', p.id, { ...p }) }
    },

    // ── 관계 ──
    async addRelation(a: string, b: string, type: Relation['type'], note: string) {
      if (!this.key) throw new Error('locked')
      const rel: Relation = { id: crypto.randomUUID(), a, b, type, note }
      await vault.putRecord(this.key, 'relation', rel.id, rel)
      this.relations.push(rel)
      return rel
    },
    async saveRelation(rel: Relation) {
      if (!this.key) throw new Error('locked')
      await vault.putRecord(this.key, 'relation', rel.id, { ...rel })
      const i = this.relations.findIndex(r => r.id === rel.id)
      if (i >= 0) this.relations[i] = rel
    },
    async deleteRelation(id: string) {
      if (!this.key) throw new Error('locked')
      await vault.deleteRecord('relation', id)
      this.relations = this.relations.filter(r => r.id !== id)
    },
  },
})

export { WrongPasswordError } from '../lib/vault'
